import { DEPRECIATION_METHOD_CODE } from 'common/constants'
import { tryCallApi } from 'common/helpers'
import { AssetLedger, FORM_MODES } from 'common/models'
import { FixedAssetsService, PresenterRevisedAcquisitionCostResponse } from 'common/open-api'
import { getAxiosSilent } from 'common/open-api/helper'
import { FeatureFlagNames } from 'common/open-features'
import { useEnableFlagRef } from 'common/open-features/hooks'
import { getIn, useFormikContext } from 'formik'
import { useCallback, useEffect, useRef } from 'react'

import { FORM_FIELDS, LEDGERS_FIELD_NAME } from '../const'
import { getAllLedgerFieldNames } from '../helper'
import { useWaitFormReady } from './use-wait-form-ready'

export const filterLedger = (ledger: Partial<AssetLedger>) =>
  [DEPRECIATION_METHOD_CODE.NEW_DECLINING_BALANCE_250, DEPRECIATION_METHOD_CODE.NEW_DECLINING_BALANCE_200].includes(
    Number(ledger.depreciation_method_code)
  ) &&
  ledger.ledger_setting_id &&
  ledger.service_life

export const shouldCalculateRevisedAcquisitionCost = (
  acquisitionCost?: number,
  transitionAt?: string,
  usageStartedAt?: string,
  ledgers?: Partial<AssetLedger>[]
) => {
  const ledgerDataAvailable = ledgers?.some(filterLedger) || false
  return ledgerDataAvailable && !!acquisitionCost && !!transitionAt && !!usageStartedAt
}

export const useCalculateRevisedAcquisitionCost = () => {
  const { setFieldValue } = useFormikContext()
  return useCallback(
    async (
      acquisitionCost?: number,
      subsidyAmount?: number,
      transitionAt?: string,
      usageStartedAt?: string,
      ledgers?: (Partial<AssetLedger> & { ledgerIndex?: number })[]
    ) => {
      const shouldCalculate = shouldCalculateRevisedAcquisitionCost(
        acquisitionCost,
        transitionAt,
        usageStartedAt,
        ledgers
      )

      if (shouldCalculate) {
        const { data } = await tryCallApi(FixedAssetsService.postFixedAssetsCalculateRevisedAcquisitionCost)(
          {
            acquisition_cost: acquisitionCost ?? 0,
            subsidy_amount: subsidyAmount ?? 0,
            transition_at: transitionAt ?? '',
            usage_started_at: usageStartedAt ?? '',
            ledgers: (ledgers ?? [])
              .map((ledger, index) => ({
                ...ledger,
                ledgerIndex: ledger.ledgerIndex ?? index,
              }))
              .filter(filterLedger)
              .map(ledger => ({
                depreciation_method: Number(ledger.depreciation_method_code),
                id: ledger.ledgerIndex + 1,
                ledger_setting_id: Number(ledger.ledger_setting_id),
                service_life: Number(ledger.service_life),
              })),
          },
          getAxiosSilent()
        )
        ;(data?.data as PresenterRevisedAcquisitionCostResponse | undefined)?.ledgers?.forEach(ledger => {
          const revisedField = getAllLedgerFieldNames(ledger.id - 1).revisedAcquisitionAmountField
          setFieldValue(revisedField, ledger.revised_acquisition_amount ?? 0)
        })
      }
    },
    []
  )
}

export const useCalculateRevisedAcquisitionCostAllLedgers = (
  disabled: boolean,
  formMode: FORM_MODES,
  acquisitionCost?: number
) => {
  const { values, getFieldProps } = useFormikContext()
  const transitionAt = getIn(values, 'transition_at')
  const usageStartedAt = getIn(values, 'usage_started_at')
  const subsidyAmount = getIn(values, 'subsidy_amount')

  const formReadyRef = useRef(false)
  useWaitFormReady(formMode, formReadyRef)

  const flagEnableRef = useRef(false)
  useEnableFlagRef(FeatureFlagNames.RevisedAcquisitionCost, flagEnableRef)

  const calculateRevisedAcquisitionCost = useCalculateRevisedAcquisitionCost()

  useEffect(() => {
    if (!formReadyRef.current || !flagEnableRef.current || disabled) {
      return
    }
    const ledgers = getFieldProps(LEDGERS_FIELD_NAME).value as AssetLedger[]
    calculateRevisedAcquisitionCost(acquisitionCost, subsidyAmount, transitionAt, usageStartedAt, ledgers)
  }, [acquisitionCost, transitionAt, usageStartedAt, subsidyAmount, disabled, calculateRevisedAcquisitionCost])
}

export const useCalculateRevisedAcquisitionCostLedger = (
  disabled: boolean,
  formMode: FORM_MODES,
  ledgerIndex: number
) => {
  const { values, getFieldProps } = useFormikContext()
  const allLedgerFields = getAllLedgerFieldNames(ledgerIndex)
  const depreciationMethodCode = getIn(values, allLedgerFields.depreciationMethodCodeField)
  const ledgerSettingId = getIn(values, allLedgerFields.ledgerSettingIdField)
  const serviceLife = getIn(values, allLedgerFields.serviceLifeField)

  const formReadyRef = useRef(false)
  useWaitFormReady(formMode, formReadyRef)

  const flagEnableRef = useRef(false)
  useEnableFlagRef(FeatureFlagNames.RevisedAcquisitionCost, flagEnableRef)

  const calculateRevisedAcquisitionCost = useCalculateRevisedAcquisitionCost()

  useEffect(() => {
    if (!formReadyRef.current || !flagEnableRef.current || disabled) {
      return
    }
    const acquisitionCost = getFieldProps(FORM_FIELDS.ACQUISITION_COST_REFERENCE).value
    const transitionAt = getFieldProps('transition_at').value
    const usageStartedAt = getFieldProps('usage_started_at').value
    const subsidyAmount = getFieldProps('subsidy_amount').value

    const ledgers = [
      {
        ledgerIndex,
        depreciation_method_code: depreciationMethodCode,
        ledger_setting_id: ledgerSettingId,
        service_life: serviceLife,
      },
    ]

    calculateRevisedAcquisitionCost(acquisitionCost, subsidyAmount, transitionAt, usageStartedAt, ledgers)
  }, [depreciationMethodCode, ledgerSettingId, serviceLife, ledgerIndex, disabled, calculateRevisedAcquisitionCost])
}
